import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Table, Button, Modal, Form, Card } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../api/axios';
import AuthContext from '../../context/AuthContext';
import { hasPermission } from '../../utils/permissions';

const ManageServiceMenus = () => {
    const { user } = useContext(AuthContext);
    const [menus, setMenus] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentMenu, setCurrentMenu] = useState({});

    const canManageMenus = hasPermission(user?.role, 'settings:menus');

    const fetchMenus = useCallback(async () => {
        if (!user) return;
        try {
            const response = await api.get('/servicemenus');
            setMenus(response.data.data);
        } catch (error) {
            console.error("Error fetching service menus:", error);
        }
    }, [user]);

    useEffect(() => {
        if (user) fetchMenus();
    }, [user, fetchMenus]);

    const handleShowModal = (menu = null) => {
        setIsEditing(menu !== null);
        setCurrentMenu(menu
            ? { ...menu }
            : { menuName: '', includeInStats: true, duration_minutes: 30, is_app_visible: false }
        );
        setShowModal(true);
    };

    const handleCloseModal = () => setShowModal(false);

    const handleSave = async () => {
        if (!user) { alert('로그인이 필요합니다.'); return; }
        try {
            if (isEditing) {
                await api.put(`/servicemenus/${currentMenu.id}`, currentMenu);
            } else {
                await api.post('/servicemenus', currentMenu);
            }
            fetchMenus();
            handleCloseModal();
        } catch (error) {
            console.error("Error saving service menu:", error);
        }
    };

    const handleToggleActive = async (id) => {
        if (!user) { alert('로그인이 필요합니다.'); return; }
        try {
            await api.put(`/servicemenus/${id}/toggle`);
            fetchMenus();
        } catch (error) {
            console.error("Error toggling service menu status:", error);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let next;
        if (type === 'checkbox') next = checked;
        else if (type === 'number') next = value === '' ? '' : parseInt(value, 10);
        else next = value;
        setCurrentMenu(prev => ({ ...prev, [name]: next }));
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) return;
        const items = Array.from(menus);
        const [moved] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, moved);
        setMenus(items);
        try {
            await api.put('/servicemenus/reorder', { order: items.map(m => ({ id: m.id })) });
        } catch (error) {
            console.error("Error saving order:", error);
            fetchMenus();
        }
    };

    return (
        <>
            <div className="page-header">
                <div className="page-header-left">
                    <h3>서비스 메뉴</h3>
                    <p>서비스 메뉴를 추가하고 순서를 관리합니다</p>
                </div>
                <div className="page-header-actions">
                    {canManageMenus && (
                        <Button variant="primary" size="sm" onClick={() => handleShowModal()}>새 메뉴 추가</Button>
                    )}
                </div>
            </div>
            <Card>
                <Card.Body>
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Table hover responsive>
                            <thead>
                                <tr>
                                    {canManageMenus && <th style={{ width: 40 }}></th>}
                                    <th>메뉴명</th>
                                    <th>소요시간</th>
                                    <th>상태</th>
                                    <th>통계 집계</th>
                                    <th>관리</th>
                                </tr>
                            </thead>
                            <Droppable droppableId="menus">
                                {(provided) => (
                                    <tbody ref={provided.innerRef} {...provided.droppableProps}>
                                        {menus.map((menu, index) => (
                                            <Draggable key={String(menu.id)} draggableId={String(menu.id)} index={index} isDragDisabled={!canManageMenus}>
                                                {(provided, snapshot) => (
                                                    <tr ref={provided.innerRef} {...provided.draggableProps}
                                                        style={{ ...provided.draggableProps.style, background: snapshot.isDragging ? '#f0f0f0' : undefined }}>
                                                        {canManageMenus && <td {...provided.dragHandleProps} style={{ cursor: 'grab', textAlign: 'center' }}>☰</td>}
                                                        <td>{menu.menuName}</td>
                                                        <td>{menu.duration_minutes ?? 30}분</td>
                                                        <td>{menu.isActive ? <span className="text-success">활성</span> : <span className="text-danger">비활성</span>}</td>
                                                        <td>{menu.includeInStats ? 'O' : 'X'}</td>
                                                        <td>
                                                            {canManageMenus && (
                                                                <>
                                                                    <Button variant="info" size="sm" onClick={() => handleShowModal(menu)}>수정</Button>{' '}
                                                                    <Button variant={menu.isActive ? 'warning' : 'success'} size="sm" onClick={() => handleToggleActive(menu.id)}>
                                                                        {menu.isActive ? '비활성화' : '활성화'}
                                                                    </Button>
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </tbody>
                                )}
                            </Droppable>
                        </Table>
                    </DragDropContext>
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? '메뉴 수정' : '새 메뉴 추가'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3" controlId="menuName">
                            <Form.Label>메뉴명</Form.Label>
                            <Form.Control type="text" name="menuName" value={currentMenu.menuName || ''} onChange={handleChange} disabled={!canManageMenus} />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="duration_minutes">
                            <Form.Label>소요시간 (분)</Form.Label>
                            <Form.Control
                                type="number"
                                name="duration_minutes"
                                min={0}
                                max={1440}
                                value={currentMenu.duration_minutes ?? 30}
                                onChange={handleChange}
                                disabled={!canManageMenus}
                            />
                            <Form.Text className="text-muted">
                                워크보드 예약 슬롯의 기본 길이. 0~1440분(하루)까지. 디테일링 등 긴 작업도 표현 가능.
                            </Form.Text>
                        </Form.Group>
                        {/* 고객 앱 노출(is_app_visible) 토글은 숨겨둔다 — 고객 앱이 아직 운영 전이라
                            사장님에게 의미 없는 선택지다. 값은 state 에 그대로 남아 신규는 false(숨김)로
                            저장되고, 기존 메뉴를 수정해도 설정해둔 값이 보존된다. 앱 출시 때 되살리면 된다. */}
                        <Form.Group className="mb-3" controlId="includeInStatsCheck">
                            <Form.Check
                                type="checkbox"
                                label="대시보드 통계에 집계"
                                name="includeInStats"
                                checked={!!currentMenu.includeInStats}
                                onChange={handleChange}
                                disabled={!canManageMenus}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>닫기</Button>
                    <Button variant="primary" onClick={handleSave} disabled={!canManageMenus}>저장</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ManageServiceMenus;
